import { PAGINATION, type PaginatedResponse } from '@utazzo/shared';
import * as XLSX from 'xlsx';

import type { DataGridState } from '@/components/data-grid';

/** Common, identical query fields every list screen sends to the API. */
export interface BaseListQuery {
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  search?: string;
}

export interface BaseQueryOptions {
  defaultSortBy: string;
  /** Minimum trimmed search length before `search` is sent. Defaults to 1. */
  minSearchLength?: number;
}

/** Map the grid's URL state to the shared base query fields. */
export function buildBaseQuery(state: DataGridState, opts: BaseQueryOptions): BaseListQuery {
  const term = state.q.trim();
  const minLen = opts.minSearchLength ?? 1;
  return {
    page: state.page,
    pageSize: state.pageSize,
    sortBy: state.sortBy ?? opts.defaultSortBy,
    sortDir: state.sortDir,
    ...(term.length >= minLen ? { search: term } : {}),
  };
}

/** One CSV column: a header label and how to read the cell from a row. */
export interface CsvColumn<TRow> {
  header: string;
  value: (row: TRow) => unknown;
}

const csvCell = (v: unknown): string => `"${String(v ?? '').replace(/"/g, '""')}"`;

/** Build a CRLF-delimited CSV string (header row + one row per item). */
export function toCsv<TRow>(
  rows: ReadonlyArray<TRow>,
  columns: ReadonlyArray<CsvColumn<TRow>>,
): string {
  const header = columns.map((c) => csvCell(c.header)).join(',');
  const lines = rows.map((row) => columns.map((c) => csvCell(c.value(row))).join(','));
  return [header, ...lines].join('\r\n');
}

/** Build a CSV and trigger a client-side download named `<filename>-<date>.csv`. */
export function downloadCsv<TRow>(
  rows: ReadonlyArray<TRow>,
  columns: ReadonlyArray<CsvColumn<TRow>>,
  filename: string,
): void {
  const csv = toCsv(rows, columns);
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Build an array-of-arrays suitable for `XLSX.utils.aoa_to_sheet`.
 * The first row is column headers (display names from `column.header`);
 * subsequent rows are the cell values. Extracted so it can be unit-tested
 * independently of the browser file-download side-effect.
 */
export function toAoa<TRow>(
  rows: ReadonlyArray<TRow>,
  columns: ReadonlyArray<CsvColumn<TRow>>,
): unknown[][] {
  return [columns.map((c) => c.header), ...rows.map((r) => columns.map((c) => c.value(r)))];
}

/**
 * Download an Excel (.xlsx) workbook of `rows` using the same `{ header, value }`
 * column spec as `downloadCsv`. The `header` field IS the visible display name
 * (so the Excel column headers are proper text, not field ids).
 *
 * The filename appears in browser-downloads with a `-YYYY-MM-DD` suffix, e.g.
 * `users-2026-06-22.xlsx`.
 */
export function downloadExcel<TRow>(
  rows: ReadonlyArray<TRow>,
  columns: ReadonlyArray<CsvColumn<TRow>>,
  filename: string,
): void {
  const aoa = toAoa(rows, columns);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${filename}-${today}.xlsx`);
}

/** Escape a value for safe insertion into HTML. */
function htmlCell(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Export `rows` to PDF using the same `{ header, value }` column spec. Opens a
 * print-ready window with a styled table and triggers the browser's print
 * dialog (Save as PDF) - no extra dependency. `title` heads the printed page.
 */
export function downloadPdf<TRow>(
  rows: ReadonlyArray<TRow>,
  columns: ReadonlyArray<CsvColumn<TRow>>,
  filename: string,
  title?: string,
): void {
  const today = new Date().toISOString().slice(0, 10);
  const head = columns.map((c) => `<th>${htmlCell(c.header)}</th>`).join('');
  const body = rows
    .map((r) => `<tr>${columns.map((c) => `<td>${htmlCell(c.value(r))}</td>`).join('')}</tr>`)
    .join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${htmlCell(
    filename,
  )}-${today}</title><style>
    @page { size: A4 landscape; margin: 12mm; }
    body { font: 11px -apple-system, Segoe UI, Roboto, sans-serif; color: #111; }
    h1 { font-size: 15px; margin: 0 0 4px; }
    .meta { color: #666; margin: 0 0 10px; font-size: 11px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #999; padding: 4px 6px; text-align: left; vertical-align: top; }
    th { background: #eee; font-weight: 700; }
    tr:nth-child(even) td { background: #f7f7f7; }
  </style></head><body>
    <h1>${htmlCell(title ?? filename)}</h1>
    <p class="meta">${rows.length} rows &middot; ${today}</p>
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    <script>window.onload = function () { window.print(); };</script>
  </body></html>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

/** Return rows minus those matching `exclude` (all rows when no predicate). */
export function excludeRows<TRow>(
  rows: ReadonlyArray<TRow>,
  exclude?: (row: TRow) => boolean,
): TRow[] {
  return exclude ? rows.filter((r) => !exclude(r)) : [...rows];
}

/**
 * Page through the server at the max page size using `query`'s current
 * filters/sort, returning every matching row. Always starts at page 1,
 * ignoring any `page` on the passed `query`. Stops on `!hasNext` or an empty
 * page. Used by the toolbar "Export all".
 */
export async function fetchAllPages<TRow, TQuery>(
  fetchPage: (query: TQuery) => Promise<PaginatedResponse<TRow>>,
  query: TQuery,
): Promise<TRow[]> {
  const all: TRow[] = [];
  let page = 1;
  for (;;) {
    const res = await fetchPage({ ...query, page, pageSize: PAGINATION.MAX_PAGE_SIZE });
    all.push(...res.data);
    if (!res.pagination.hasNext || res.data.length === 0) break;
    page += 1;
  }
  return all;
}
